import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Check, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { Reveal } from "@/components/site/Reveal";

export default function ProductDetail() {
    const { slug } = useParams();
    const { t } = useLang();
    const [product, setProduct] = useState(null);
    const [active, setActive] = useState(0);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        setProduct(null); setActive(0); setNotFound(false);
        api.get(`/products/${slug}`).then((r) => setProduct(r.data)).catch(() => setNotFound(true));
    }, [slug]);

    if (notFound) {
        return (
            <div className="bg-background min-h-screen">
                <Header />
                <div className="max-w-[1400px] mx-auto px-6 pt-48 pb-40 text-center">
                    <h1 className="font-heading font-black text-4xl text-obsidian">{t.detail.notFound}</h1>
                    <Link to="/products" className="eyebrow text-cyan mt-6 inline-block">← {t.detail.backToDevices}</Link>
                </div>
                <Footer />
            </div>
        );
    }

    if (!product) return <div className="bg-background min-h-screen"><Header /><div className="pt-48 text-center eyebrow text-muted-foreground">{t.detail.loading}</div></div>;

    const gallery = product.gallery?.length ? product.gallery : [product.image];

    return (
        <div className="bg-background min-h-screen" data-testid="product-detail">
            <Header />

            <section className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-32 pb-20">
                <Link to="/products" data-testid="back-to-products" className="eyebrow inline-flex items-center gap-2 text-muted-foreground hover:text-obsidian transition-colors mb-10">
                    <ArrowLeft size={14} /> {t.detail.backToDevices}
                </Link>

                <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-start">
                    <div className="lg:sticky lg:top-28">
                        <motion.div
                            key={active}
                            initial={{ opacity: 0.4, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5 }}
                            className="relative rounded-2xl overflow-hidden bg-secondary border border-border aspect-square"
                        >
                            <img src={gallery[active]} alt={product.name} className="w-full h-full object-cover" />
                            <span className="absolute top-5 left-5 eyebrow glass rounded-full px-3 py-1 text-obsidian">{product.category}</span>
                        </motion.div>
                        {gallery.length > 1 && (
                            <div className="flex gap-3 mt-4" data-testid="gallery-thumbs">
                                {gallery.map((g, i) => (
                                    <button key={i} onClick={() => setActive(i)} data-testid={`thumb-${i}`}
                                        className={`w-20 h-20 rounded-lg overflow-hidden border-2 transition-colors ${active === i ? "border-cyan" : "border-transparent opacity-60 hover:opacity-100"}`}>
                                        <img src={g} alt="" className="w-full h-full object-cover" />
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    <div>
                        <div className="eyebrow text-cyan mb-4">{product.tagline}</div>
                        <h1 className="font-heading font-black text-5xl sm:text-6xl tracking-tighter text-obsidian leading-[0.9]">{product.name}</h1>
                        <div className="mt-6 flex items-baseline gap-4">
                            <span className="font-heading font-black text-3xl text-obsidian">${product.price?.toLocaleString()}</span>
                            <span className="eyebrow text-muted-foreground">{t.detail.incl}</span>
                        </div>
                        <p className="mt-8 text-lg text-muted-foreground leading-relaxed">{product.description}</p>

                        {product.benefits?.length > 0 && (
                            <div className="mt-10 grid sm:grid-cols-2 gap-3">
                                {product.benefits.map((b, i) => (
                                    <div key={i} className="flex items-center gap-3">
                                        <span className="grid place-items-center w-6 h-6 rounded-full bg-cyan/15 text-cyan shrink-0"><Check size={14} strokeWidth={3} /></span>
                                        <span className="text-sm text-obsidian font-medium">{b}</span>
                                    </div>
                                ))}
                            </div>
                        )}

                        <Link to="/contact" data-testid="detail-cta" className="group eyebrow inline-flex items-center gap-2 mt-12 rounded-full bg-obsidian text-white px-8 py-4 hover:-translate-y-0.5 hover:bg-cyan hover:text-obsidian transition-all duration-300">
                            {t.cta.requestPricing} <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                        </Link>
                    </div>
                </div>
            </section>

            <section className="max-w-[1400px] mx-auto px-6 lg:px-10 pb-32">
                <div className="grid lg:grid-cols-12 gap-6">
                    <Reveal className="lg:col-span-7 bg-obsidian text-white rounded-2xl p-10 grain relative overflow-hidden">
                        <div className="eyebrow text-cyan mb-8">{t.detail.capabilities}</div>
                        <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-5">
                            {product.features?.map((f, i) => (
                                <li key={i} className="flex items-start gap-3 border-b border-white/10 pb-4">
                                    <span className="font-mono-tech text-cyan text-sm">{String(i + 1).padStart(2, "0")}</span>
                                    <span className="text-white/85">{f}</span>
                                </li>
                            ))}
                        </ul>
                    </Reveal>

                    <Reveal delay={0.08} className="lg:col-span-5 bg-card border border-border rounded-2xl p-10">
                        <div className="eyebrow text-cyan mb-8">{t.detail.specifications}</div>
                        <dl className="divide-y divide-border">
                            {product.specs?.map((s, i) => (
                                <div key={i} className="flex items-center justify-between py-4">
                                    <dt className="text-muted-foreground text-sm">{s.label}</dt>
                                    <dd className="font-mono-tech text-obsidian font-medium text-sm">{s.value}</dd>
                                </div>
                            ))}
                        </dl>
                    </Reveal>
                </div>
            </section>

            <Footer />
        </div>
    );
}
